import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface TimePickerProps {
  value: string; // Format: "HH:mm" (24-hour)
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  min?: string; // Minimum time in "HH:mm" format
  max?: string; // Maximum time in "HH:mm" format
  step?: number; // Minutes step (default: 15)
  /** "HH:mm" to start the picker on when there is no value yet — e.g. the
   * conference's first slot — instead of 12:00 AM. */
  defaultTime?: string;
}

export default function TimePicker({
  value,
  onChange,
  className = '',
  disabled = false,
  min,
  max,
  step = 15,
  defaultTime,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const seed = (value || defaultTime || '00:00').split(':');
  const [hours, setHours] = useState(seed[0] || '00');
  const [minutes, setMinutes] = useState(seed[1] || '00');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Parse value on mount and when value changes; with no value, sit on the
  // suggested default so the picker opens where the conference actually starts.
  useEffect(() => {
    const src = value || defaultTime || '';
    if (src) {
      const [h, m] = src.split(':');
      setHours(h || '00');
      setMinutes(m || '00');
    } else {
      setHours('00');
      setMinutes('00');
    }
  }, [value, defaultTime]);

  // The dropdown is portaled to <body> and placed from the trigger's screen
  // rect, so a card's overflow or stacking context can never clip it. Flip
  // above the trigger when there isn't room below.
  const place = () => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return;
    const height = dropdownRef.current?.offsetHeight || 340;
    const width = Math.max(280, r.width);
    const below = r.bottom + 4;
    const top = below + height > window.innerHeight - 8 && r.top - height - 4 > 8 ? r.top - height - 4 : below;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos({ top, left, width });
  };
  useLayoutEffect(() => {
    if (!isOpen) return;
    place();
    // Bring the selected hour into view once the list exists.
    const sel = dropdownRef.current?.querySelector<HTMLElement>('[data-hour][aria-selected="true"]');
    sel?.scrollIntoView({ block: 'center' });
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen]);

  // Close on outside click (the dropdown lives outside the container, so check both)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (containerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return { value: hour, label: hour === '00' ? '12' : (i > 12 ? (i - 12).toString() : i.toString()), period: i < 12 ? 'AM' : 'PM' };
  });

  // Generate minute options based on step
  const minuteOptions = Array.from({ length: 60 / step }, (_, i) => {
    const minute = (i * step).toString().padStart(2, '0');
    return { value: minute, label: minute };
  });

  const handleHourChange = (newHour: string) => {
    setHours(newHour);
    const newValue = `${newHour}:${minutes}`;
    if (isValidTime(newValue)) {
      onChange(newValue);
    }
  };

  const handleMinuteChange = (newMinute: string) => {
    setMinutes(newMinute);
    const newValue = `${hours}:${newMinute}`;
    if (isValidTime(newValue)) {
      onChange(newValue);
    }
  };

  const isValidTime = (time: string): boolean => {
    if (min && time < min) return false;
    if (max && time > max) return false;
    return true;
  };

  const formatDisplayTime = (): string => {
    if (!hours || !minutes) return '';
    const hourNum = parseInt(hours, 10);
    const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    const period = hourNum < 12 ? 'AM' : 'PM';
    return `${displayHour}:${minutes} ${period}`;
  };

  const currentHourNum = parseInt(hours, 10);
  const displayHour = currentHourNum === 0 ? 12 : currentHourNum > 12 ? currentHourNum - 12 : currentHourNum;
  const period = currentHourNum < 12 ? 'AM' : 'PM';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Input Display */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-4 py-2 border rounded-lg text-left
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-300'}
          ${disabled ? 'bg-gray-100' : 'bg-white'}
        `}
        aria-label="Select time"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex items-center justify-between">
          <span className="text-gray-900 font-medium whitespace-nowrap">
            {value ? formatDisplayTime() : 'Select time'}
          </span>
          <ChevronDown 
            size={16} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown (portaled so nothing above it can clip or cover it) */}
      {isOpen && !disabled && typeof document !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, minWidth: pos?.width ?? 280, zIndex: 10000 }}
          className="bg-white border border-gray-300 rounded-lg shadow-xl p-4"
        >
          <div className="flex items-center gap-4">
            {/* Hours Column */}
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-2 text-center">Hour</label>
              <div className="flex flex-col items-center gap-1 max-h-48 overflow-y-auto">
                {hourOptions.map((option) => {
                  const isSelected = option.value === hours;
                  const hourNum = parseInt(option.value, 10);
                  const isDisabled = !!(
                    (min && `${option.value}:${minutes}` < min) ||
                    (max && `${option.value}:${minutes}` > max));
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      data-hour={option.value}
                      onClick={() => handleHourChange(option.value)}
                      disabled={isDisabled}
                      className={`
                        w-16 px-3 py-2 rounded-lg text-sm font-medium transition-all
                        ${isSelected
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }
                        ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      aria-selected={isSelected}
                    >
                      <div className="flex flex-col items-center">
                        <span>{option.label}</span>
                        <span className="text-[10px] opacity-75">{option.period}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Separator */}
            <div className="text-2xl font-bold text-gray-400 py-8">:</div>

            {/* Minutes Column */}
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-2 text-center">Minutes</label>
              <div className="flex flex-col items-center gap-1 max-h-48 overflow-y-auto">
                {minuteOptions.map((option) => {
                  const isSelected = option.value === minutes;
                  const isDisabled = !!(
                    (min && `${hours}:${option.value}` < min) ||
                    (max && `${hours}:${option.value}` > max));
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleMinuteChange(option.value)}
                      disabled={isDisabled}
                      className={`
                        w-16 px-3 py-2 rounded-lg text-sm font-medium transition-all
                        ${isSelected
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }
                        ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      aria-selected={isSelected}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Current Selection Display */}
          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <div className="text-sm text-gray-600 mb-1">Selected Time</div>
            <div className="text-lg font-bold text-gray-900">
              {formatDisplayTime()}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
